import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tab, Tabs, Alert, Spinner, Table, Button, Form, Row, Col } from 'react-bootstrap';
import API_URL from '../../config';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

// ─── Sub-componentes ─────────────────────────────────────────────────────────

/**
 * Tabela editável para ftopo, fforma, finfra, ffre (só têm id, descricao, fator).
 */
function FatorSimpleTable({ title, rows, onChange }) {
    const handleFator = (i, val) => {
        const updated = deepClone(rows);
        updated[i].fator = parseFloat(val) || 0;
        onChange(updated);
    };
    return (
        <div className="mb-4">
            <h6 className="fw-bold mb-2">{title}</h6>
            <Table bordered size="sm" responsive>
                <thead className="table-secondary">
                    <tr>
                        <th>Descrição</th>
                        <th style={{ width: 120 }}>Fator</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.id ?? i}>
                            <td className="align-middle">{r.descricao}</td>
                            <td>
                                <Form.Control
                                    type="number" size="sm" step="0.01" min="0"
                                    value={r.fator}
                                    onChange={e => handleFator(i, e.target.value)}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}

/**
 * Tabela editável para farea e fprof (têm id, descricao, limite_sup, fator).
 */
function FatorLimiteTable({ title, rows, onChange }) {
    const handleField = (i, field, val) => {
        const updated = deepClone(rows);
        updated[i][field] = parseFloat(val) || 0;
        onChange(updated);
    };
    return (
        <div className="mb-4">
            <h6 className="fw-bold mb-2">{title}</h6>
            <Table bordered size="sm" responsive>
                <thead className="table-secondary">
                    <tr>
                        <th>Descrição</th>
                        <th style={{ width: 130 }}>Limite superior</th>
                        <th style={{ width: 120 }}>Fator</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.id ?? i}>
                            <td className="align-middle">{r.descricao}</td>
                            <td>
                                <Form.Control
                                    type="number" size="sm" step="0.01" min="0"
                                    value={r.limite_sup}
                                    onChange={e => handleField(i, 'limite_sup', e.target.value)}
                                />
                            </td>
                            <td>
                                <Form.Control
                                    type="number" size="sm" step="0.001" min="0"
                                    value={r.fator}
                                    onChange={e => handleField(i, 'fator', e.target.value)}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}

/**
 * Tabela editável de índices de benfeitoria.
 * indices = { "Casa": { "Padrão Rústico": {intervalo, vida_referencial, valor_residual}, ... }, ... }
 */
function IndicesBenfeitoriaTable({ indices, onChange }) {
    if (!indices) return null;

    const handleField = (grupo, padrao, field, val) => {
        const updated = deepClone(indices);
        updated[grupo][padrao][field] = parseFloat(val) || 0;
        onChange(updated);
    };

    return (
        <Table bordered size="sm" responsive>
            <thead className="table-dark">
                <tr>
                    <th>Tipo</th>
                    <th>Padrão</th>
                    <th style={{ width: 120 }}>Intervalo (R8N×)</th>
                    <th style={{ width: 130 }}>Vida Ref. (anos)</th>
                    <th style={{ width: 130 }}>Val. Residual (%)</th>
                </tr>
            </thead>
            <tbody>
                {Object.entries(indices).map(([grupo, padroes]) =>
                    Object.entries(padroes).map(([padrao, vals], pIdx) => (
                        <tr key={`${grupo}-${padrao}`}>
                            {pIdx === 0 && (
                                <td rowSpan={Object.keys(padroes).length}
                                    className="align-middle fw-bold text-center bg-light">
                                    {grupo}
                                </td>
                            )}
                            <td className="align-middle">{padrao}</td>
                            <td>
                                <Form.Control
                                    type="number" size="sm" step="0.001" min="0"
                                    value={vals.intervalo}
                                    onChange={e => handleField(grupo, padrao, 'intervalo', e.target.value)}
                                />
                            </td>
                            <td>
                                <Form.Control
                                    type="number" size="sm" step="1" min="1"
                                    value={vals.vida_referencial}
                                    onChange={e => handleField(grupo, padrao, 'vida_referencial', e.target.value)}
                                />
                            </td>
                            <td>
                                <Form.Control
                                    type="number" size="sm" step="0.01" min="0" max="1"
                                    value={vals.valor_residual}
                                    onChange={e => handleField(grupo, padrao, 'valor_residual', e.target.value)}
                                />
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </Table>
    );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

function ParametrosForm() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Estado local dos parâmetros (editável)
    const [fatoresLote, setFatoresLote] = useState(null);
    const [indicesBenfeitoria, setIndicesBenfeitoria] = useState(null);

    // R8N (antes em Configurações)
    const [r8nValue, setR8nValue] = useState('');
    const [currentR8n, setCurrentR8n] = useState(null);
    const [savingR8n, setSavingR8n] = useState(false);

    const loadR8n = useCallback(() => {
        axios.get(`${API_URL}/api/configuration`, { withCredentials: true })
            .then(r => { setCurrentR8n(r.data); setR8nValue(r.data.r8n_value.toString()); })
            .catch(() => {});
    }, []);

    const loadParams = useCallback(() => {
        setLoading(true);
        setError('');
        axios.get(`${API_URL}/api/parametros`, { withCredentials: true })
            .then(res => {
                setFatoresLote(deepClone(res.data.fatores_lote));
                setIndicesBenfeitoria(deepClone(res.data.indices_benfeitoria));
            })
            .catch(() => setError('Erro ao carregar parâmetros.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadParams(); loadR8n(); }, [loadParams, loadR8n]);

    const handleSaveR8n = async (e) => {
        e.preventDefault();
        setSavingR8n(true); setError(''); setSuccess('');
        try {
            const res = await axios.post(`${API_URL}/api/configuration`,
                { r8n_value: parseFloat(r8nValue) }, { withCredentials: true });
            setCurrentR8n(res.data.configuration);
            setSuccess('Valor R8N atualizado com sucesso!');
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao salvar R8N.');
        } finally {
            setSavingR8n(false);
        }
    };

    const handleResetR8n = async () => {
        setSavingR8n(true); setError(''); setSuccess('');
        try {
            const res = await axios.post(`${API_URL}/api/configuration/reset`, {}, { withCredentials: true });
            setCurrentR8n(res.data.configuration);
            setR8nValue(res.data.configuration.r8n_value.toString());
            setSuccess('R8N restaurado para o valor padrão!');
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao restaurar R8N.');
        } finally {
            setSavingR8n(false);
        }
    };

    const handleSave = async () => {
        setSaving(true); setError(''); setSuccess('');
        try {
            await axios.post(`${API_URL}/api/parametros`, {
                fatores_lote: fatoresLote,
                indices_benfeitoria: indicesBenfeitoria,
            }, { withCredentials: true });
            setSuccess('Parâmetros salvos com sucesso!');
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao salvar parâmetros.');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Restaurar todos os parâmetros para os valores padrão (PDF/IBAPE)?')) return;
        setSaving(true); setError(''); setSuccess('');
        try {
            await axios.post(`${API_URL}/api/parametros/reset`, {}, { withCredentials: true });
            setSuccess('Parâmetros restaurados para os valores padrão!');
            loadParams();
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao restaurar parâmetros.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="bc-page">
                <div className="bc-loading">
                    <Spinner animation="border" />
                    <span>Carregando parâmetros...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bc-page">
            <div className="container" style={{ maxWidth: '960px' }}>

                <div className="bc-page-header">
                    <h2>Parâmetros de Cálculo</h2>
                    <p>Configure os coeficientes utilizados nos módulos de Benfeitoria e Lote/Loteamento.</p>
                </div>

                {error   && <Alert variant="danger"  dismissible onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

                <Tabs defaultActiveKey="geral" className="mb-3" id="parametros-tabs">

                    {/* ── Tab Geral / R8N ── */}
                    <Tab eventKey="geral" title="Geral">
                        <div className="bc-section">
                            <div className="bc-section-title">
                                <span className="bc-section-num">1</span>
                                Valor de Referência R8N (R$/m²)
                            </div>
                            <div className="bc-help mb-3">
                                O R8N é o custo unitário básico de construção aplicado em todos os cálculos de benfeitoria.
                                Recomenda-se atualizar mensalmente.
                            </div>

                            {currentR8n && (
                                <div className="bc-config-hero mb-3">
                                    <div className="icon">📊</div>
                                    <div>
                                        <div className="lbl">Valor R8N atual</div>
                                        <div className="val">R$ {currentR8n.r8n_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                                        <div className="date">
                                            Atualizado em {currentR8n.last_updated
                                                ? new Date(currentR8n.last_updated).toLocaleString('pt-BR') : '—'}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSaveR8n}>
                                <Row className="align-items-end g-2">
                                    <Col md={4}>
                                        <Form.Label className="fw-bold">Novo valor R8N</Form.Label>
                                        <Form.Control
                                            type="number" step="0.01" min="0.01"
                                            placeholder="Ex: 1269.01"
                                            value={r8nValue}
                                            onChange={e => setR8nValue(e.target.value)}
                                            required
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <Button type="submit" className="bc-btn bc-btn-primary" disabled={savingR8n}>
                                            {savingR8n ? <><Spinner animation="border" size="sm" className="me-2" />Salvando...</> : 'Salvar R8N'}
                                        </Button>
                                    </Col>
                                    <Col md="auto">
                                        <Button variant="outline-secondary" type="button" onClick={handleResetR8n} disabled={savingR8n}>
                                            Restaurar Padrão
                                        </Button>
                                    </Col>
                                </Row>
                            </form>
                        </div>
                    </Tab>

                    {/* ── Tab Benfeitoria ── */}
                    <Tab eventKey="benfeitoria" title="Benfeitoria (Ross-Heidecke)">
                        <div className="bc-section">
                            <div className="bc-section-title">
                                <span className="bc-section-num">1</span>
                                Índices por Tipo e Padrão Construtivo
                            </div>
                            <div className="bc-help mb-3">
                                Fonte: IBAPE/SP — Relatório Técnico PGV Piraju/SP. O <strong>Intervalo</strong> é o multiplicador aplicado ao R8N;
                                a <strong>Vida Referencial</strong> e o <strong>Valor Residual</strong> alimentam a fórmula Ross-Heidecke.
                                Valor Residual deve ser entre 0 e 1 (ex: 0.20 = 20%).
                            </div>
                            <IndicesBenfeitoriaTable
                                indices={indicesBenfeitoria}
                                onChange={setIndicesBenfeitoria}
                            />
                        </div>
                    </Tab>

                    {/* ── Tab Lote ── */}
                    <Tab eventKey="lote" title="Lote / Loteamento (Homogeneização)">
                        <div className="bc-section">
                            <div className="bc-section-title">
                                <span className="bc-section-num">2</span>
                                Fatores de Homogeneização (PGV/IBAPE)
                            </div>
                            <div className="bc-help mb-3">
                                Valores aplicados na fórmula: <strong>Vh = Vo × Ftopo × Fforma × Finfra × Ffre × Fprof × Farea × (Fencravado se aplicável)</strong>.
                            </div>

                            <Row>
                                <Col md={6}>
                                    {fatoresLote?.ftopo && (
                                        <FatorSimpleTable
                                            title="Topografia (Ftopo)"
                                            rows={fatoresLote.ftopo}
                                            onChange={rows => setFatoresLote(f => ({ ...f, ftopo: rows }))}
                                        />
                                    )}
                                </Col>
                                <Col md={6}>
                                    {fatoresLote?.fforma && (
                                        <FatorSimpleTable
                                            title="Forma (Fforma)"
                                            rows={fatoresLote.fforma}
                                            onChange={rows => setFatoresLote(f => ({ ...f, fforma: rows }))}
                                        />
                                    )}
                                </Col>
                                <Col md={6}>
                                    {fatoresLote?.finfra && (
                                        <FatorSimpleTable
                                            title="Infraestrutura (Finfra)"
                                            rows={fatoresLote.finfra}
                                            onChange={rows => setFatoresLote(f => ({ ...f, finfra: rows }))}
                                        />
                                    )}
                                </Col>
                                <Col md={6}>
                                    {fatoresLote?.ffre && (
                                        <FatorSimpleTable
                                            title="Frentes / Frequência de Uso (Ffre)"
                                            rows={fatoresLote.ffre}
                                            onChange={rows => setFatoresLote(f => ({ ...f, ffre: rows }))}
                                        />
                                    )}
                                </Col>
                                <Col md={6}>
                                    {fatoresLote?.farea && (
                                        <FatorLimiteTable
                                            title="Área (Farea) — razão A/Ap"
                                            rows={fatoresLote.farea}
                                            onChange={rows => setFatoresLote(f => ({ ...f, farea: rows }))}
                                        />
                                    )}
                                </Col>
                                <Col md={6}>
                                    {fatoresLote?.fprof && (
                                        <FatorLimiteTable
                                            title="Profundidade (Fprof) — razão T/P"
                                            rows={fatoresLote.fprof}
                                            onChange={rows => setFatoresLote(f => ({ ...f, fprof: rows }))}
                                        />
                                    )}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Fator de lote encravado</Form.Label>
                                        <Form.Control
                                            type="number" step="0.01" min="0" max="1"
                                            value={fatoresLote?.encravado_fator ?? 0.75}
                                            onChange={e => setFatoresLote(f => ({ ...f, encravado_fator: parseFloat(e.target.value) || 0 }))}
                                        />
                                        <Form.Text className="text-muted">Padrão: 0,75</Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Fator de oferta (Foferta)</Form.Label>
                                        <Form.Control
                                            type="number" step="0.01" min="0"
                                            value={fatoresLote?.oferta_fator ?? 1.00}
                                            onChange={e => setFatoresLote(f => ({ ...f, oferta_fator: parseFloat(e.target.value) || 0 }))}
                                        />
                                        <Form.Text className="text-muted">Padrão: 1,00 (neutro)</Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </Tab>
                </Tabs>

                {/* Ações */}
                <Row className="g-2 mt-2">
                    <Col md={5}>
                        <Button
                            className="bc-btn bc-btn-primary w-100"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? <><Spinner animation="border" size="sm" className="me-2" />Salvando...</> : 'Salvar Parâmetros'}
                        </Button>
                    </Col>
                    <Col md={4}>
                        <Button
                            variant="outline-secondary"
                            className="w-100"
                            onClick={handleReset}
                            disabled={saving}
                        >
                            Restaurar Padrões (PDF)
                        </Button>
                    </Col>
                </Row>

                <div className="bc-info-box mt-4">
                    <h6>Informações</h6>
                    <ul>
                        <li>Os valores padrão foram extraídos do Relatório Técnico PGV — Piraju/SP (IBAPE/SP).</li>
                        <li>Alterações afetam apenas novos cálculos; registros salvos mantêm os valores originais.</li>
                        <li>O botão "Restaurar Padrões" remove todas as customizações e recarrega os valores do PDF.</li>
                        <li>O R8N (valor de referência) é configurado na aba <strong>Geral</strong>.</li>
                    </ul>
                </div>

            </div>
        </div>
    );
}

export default ParametrosForm;
